/**
 * サーバーノード一覧 `/nodes`。
 *
 * 状態は API が返す `displayStatus`（5 値）だけで表示する。`lastSeenAt` から
 * UI 側で「応答なし」を推測すると、判定ロジックが CP と SPA の 2 か所に散る（§4.3）。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Plus, RefreshCw, Wrench } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  NodeStatusBadge,
  PageHeader,
  StatusBadge,
  UsageBar,
  formatBytes,
  formatCount,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import {
  NODE_DISPLAY_STATUS,
  NODE_ROLE,
  ROLLOUT_RING,
  labelOf,
} from '@mail-sv/shared';
import { api, newIdempotencyKey } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { toNodeSummary, type ApiNode } from '../lib/node-view.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ConfirmTextDialog, FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { ListEmpty, QueryError, RefetchingDot, errorMessageOf } from '../components/states.js';
import { RID } from './ids.js';
import type {
  CountedList,
  NodeCreateResult,
  NodeEnrollTokenResult,
  NodeGroupSummary,
  NodeSummary,
} from '../types/api.js';

const routeApi = getRouteApi(RID.nodes);

export function NodesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);
  const canDelete = usePermission(OPS_PERMISSION.nodeDelete);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<NodeSummary | null>(null);

  // API は DB 語彙で返すので、境界（lib/node-view.ts）で画面の語彙へ寄せる。
  const list = useCursorList<ApiNode>({
    path: '/nodes',
    keyFor: (params) => qk.nodes(params),
    filters: {
      q: search.q,
      status: search.status,
      region: search.region,
      role: search.role,
      ring: search.ring,
      agentVersion: search.agentVersion,
    },
    refetchInterval: 10_000,
  });

  const rows = React.useMemo(() => list.rows.map(toNodeSummary), [list.rows]);

  const setSearch = React.useCallback(
    (patch: Partial<typeof search>) => {
      void navigate({ search: (prev) => ({ ...prev, ...patch }) });
    },
    [navigate],
  );

  const columns = useNodeColumns({ canCommand, canWrite, canDelete, onDelete: setDeleteTarget });

  return (
    <>
      <PageHeader
        title="サーバーノード"
        description="メールデータプレーンを構成する物理・仮想ノードの一覧。"
        actions={
          <>
            <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
            {canWrite ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus aria-hidden />
                ノードを追加
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="ホスト名 / IP で絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: {} })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(NODE_DISPLAY_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
          {
            key: 'role',
            label: '役割',
            value: search.role,
            options: optionsFrom(NODE_ROLE, labelOf),
            onChange: (v) => setSearch({ role: v as typeof search.role }),
          },
          {
            key: 'ring',
            label: 'リング',
            value: search.ring,
            options: optionsFrom(ROLLOUT_RING, labelOf),
            onChange: (v) => setSearch({ ring: v as typeof search.ring }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.query.isLoading}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="ノード"
            onClearFilter={() => void navigate({ search: {} })}
            action={
              canWrite ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus aria-hidden />
                  最初のノードを追加
                </Button>
              ) : undefined
            }
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <CreateNodeDialog open={createOpen} onOpenChange={setCreateOpen} />
      <DeleteNodeDialog node={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </>
  );
}

/* ------------------------------------------------------------------ 列定義 */

function useNodeColumns({
  canCommand,
  canWrite,
  canDelete,
  onDelete,
}: {
  canCommand: boolean;
  canWrite: boolean;
  canDelete: boolean;
  onDelete: (node: NodeSummary) => void;
}): Array<DataTableColumn<NodeSummary>> {
  return React.useMemo(
    () => [
      {
        id: 'status',
        header: '状態',
        width: 'w-36',
        cell: (n) => <NodeStatusBadge displayStatus={n.displayStatus} />,
      },
      {
        id: 'hostname',
        header: 'ホスト名',
        cell: (n) => (
          <div className="min-w-0">
            <Link
              to="/nodes/$nodeId"
              params={{ nodeId: n.id }}
              search={{ tab: 'overview', range: '24h' }}
              className="block truncate font-medium text-foreground hover:text-primary"
            >
              {n.hostname}
            </Link>
            <span className="block truncate text-xs text-muted-foreground">{n.publicIp ?? '—'}</span>
          </div>
        ),
      },
      {
        id: 'region',
        header: 'リージョン',
        hideBelow: 'lg',
        cell: (n) => <span className="text-sm">{n.region}</span>,
      },
      {
        id: 'role',
        header: '役割',
        hideBelow: 'lg',
        cell: (n) => <span className="text-sm">{labelOf(n.role)}</span>,
      },
      {
        id: 'domains',
        header: 'ドメイン',
        align: 'right',
        hideBelow: 'md',
        cell: (n) => formatCount(n.domainCount),
      },
      {
        id: 'mailboxes',
        header: 'メールボックス',
        align: 'right',
        hideBelow: 'md',
        cell: (n) => formatCount(n.mailboxCount),
      },
      {
        id: 'disk',
        header: 'ディスク',
        width: 'w-40',
        hideBelow: 'md',
        cell: (n) => (
          <UsageBar used={n.diskUsedBytes} total={n.diskTotalBytes} formatValue={(v) => formatBytes(v)} />
        ),
      },
      {
        id: 'agent',
        header: 'エージェント',
        hideBelow: 'lg',
        cell: (n) => (
          <div className="space-y-0.5">
            <span className="block text-xs tabular">{n.agentVersion ?? '—'}</span>
            <span className="block text-xs text-muted-foreground">{formatRelative(n.lastSeenAt)}</span>
          </div>
        ),
      },
      {
        id: 'apply',
        header: '設定',
        hideBelow: 'lg',
        cell: (n) => <StatusBadge status={n.applyState} size="sm" />,
      },
      {
        id: 'actions',
        header: <span className="sr-only">操作</span>,
        width: 'w-12',
        align: 'right',
        cell: (n) => (
          <RowActions
            node={n}
            canCommand={canCommand}
            canWrite={canWrite}
            canDelete={canDelete}
            onDelete={onDelete}
          />
        ),
      },
    ],
    [canCommand, canWrite, canDelete, onDelete],
  );
}

/* -------------------------------------------------------------- 行アクション */

function RowActions({
  node,
  canCommand,
  canWrite,
  canDelete,
  onDelete,
}: {
  node: NodeSummary;
  canCommand: boolean;
  canWrite: boolean;
  canDelete: boolean;
  onDelete: (node: NodeSummary) => void;
}): React.ReactElement {
  const qc = useQueryClient();

  const command = useMutation({
    mutationFn: (kind: string) => api.post(`/nodes/${node.id}/commands`, { kind, args: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodes() });
    },
  });

  const maintenance = useMutation({
    mutationFn: (on: boolean) => api.patch(`/nodes/${node.id}`, { maintenanceMode: on }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodes() });
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${node.hostname} の操作`}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{node.hostname}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/nodes/$nodeId" params={{ nodeId: node.id }} search={{ tab: 'overview', range: '24h' }}>
            詳細を開く
          </Link>
        </DropdownMenuItem>
        {canCommand ? (
          <>
            <DropdownMenuItem onSelect={() => command.mutate('apply.state')} disabled={command.isPending}>
              <RefreshCw className="size-4" aria-hidden />
              設定を今すぐ再取得させる
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => command.mutate('auth.flush')} disabled={command.isPending}>
              認証キャッシュをフラッシュ
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => command.mutate('diag.collect')} disabled={command.isPending}>
              診断情報を収集
            </DropdownMenuItem>
          </>
        ) : null}
        {canWrite ? (
          <DropdownMenuItem
            onSelect={() => maintenance.mutate(!node.maintenanceMode)}
            disabled={maintenance.isPending}
          >
            <Wrench className="size-4" aria-hidden />
            {node.maintenanceMode ? 'メンテナンスを解除' : 'メンテナンスモードにする'}
          </DropdownMenuItem>
        ) : null}
        {canDelete ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onSelect={() => onDelete(node)}>
              ノードを削除
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ---------------------------------------------------------- ノード追加 */

function CreateNodeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [form, setForm] = React.useState({
    nodePoolId: '',
    hostname: '',
    displayName: '',
    region: '',
    role: 'mx+submission',
    maxDomains: '',
    maxMailboxes: '2000',
  });
  const [result, setResult] = React.useState<
    { nodeId: string; token: NodeEnrollTokenResult } | null
  >(null);
  // 再送しても 1 台しか作られないよう、ダイアログを開いている間はキーを固定する。
  const idemKey = React.useRef(newIdempotencyKey());

  // ノードは必ずプールに属する（`nodePoolId` は必須）。プールが 1 つも無いと
  // ノードを登録できないので、その旨をダイアログの中で言う。
  const pools = useQuery({
    queryKey: qk.nodeGroups(),
    queryFn: () => api.get<CountedList<NodeGroupSummary>>('/node-groups'),
    enabled: open,
  });

  React.useEffect(() => {
    if (open) idemKey.current = newIdempotencyKey();
  }, [open]);

  React.useEffect(() => {
    const first = pools.data?.items[0];
    if (first && form.nodePoolId === '') {
      setForm((prev) => ({ ...prev, nodePoolId: first.id, region: prev.region || first.region }));
    }
  }, [pools.data, form.nodePoolId]);

  /*
   * **作成と登録トークンの発行は別のルート**（`POST /nodes` → `POST /nodes/:id/enroll-token`）。
   * 以前ここは 1 回の POST で `{node, enrollToken, enrollTokenExpiresAt}` が返る前提で
   * 書かれていたが、サーバーの応答は `{id}` だけ。しかも必須の `nodePoolId` /
   * `displayName` を送っておらず、**「ノードを追加」は常に 400 で失敗**していた。
   */
  const mut = useMutation({
    mutationFn: async () => {
      const created = await api.post<NodeCreateResult>(
        '/nodes',
        {
          nodePoolId: form.nodePoolId,
          hostname: form.hostname.trim(),
          displayName: form.displayName.trim() === '' ? form.hostname.trim() : form.displayName.trim(),
          region: form.region.trim(),
          role: form.role,
          ...(form.maxDomains.trim() === '' ? {} : { maxDomains: Number(form.maxDomains) }),
          ...(form.maxMailboxes.trim() === '' ? {} : { maxMailboxes: Number(form.maxMailboxes) }),
        },
        { idempotencyKey: idemKey.current },
      );
      const token = await api.post<NodeEnrollTokenResult>(`/nodes/${created.id}/enroll-token`, {
        expiresInHours: 24,
      });
      return { nodeId: created.id, token };
    },
    onSuccess: (res) => {
      setResult(res);
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: qk.nodes() });
    },
  });

  const valid =
    form.nodePoolId.length > 0 && form.hostname.trim().length > 2 && form.region.trim().length > 0;

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title="ノードを追加"
        description="登録トークンを発行します。ノード側のインストーラにこのトークンを渡すとエージェントが自己登録します。"
        submitLabel="登録トークンを発行"
        pending={mut.isPending}
        error={mut.isError ? mut.error : undefined}
        disabled={!valid}
        onSubmit={() => mut.mutate()}
        wide
      >
        {pools.data && pools.data.items.length === 0 ? (
          <Alert variant="warning">
            <AlertDescription>
              ノードプールが 1 つもありません。ノードは必ずプールに属するため、先にプールを作成してください。
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="ノードプール" required hint="配置先。専用プールは特定の契約組織だけを収容します。">
            <NativeSelect
              value={form.nodePoolId}
              onChange={(v) => setForm({ ...form, nodePoolId: v })}
              disabled={pools.isLoading}
              options={(pools.data?.items ?? []).map((p) => ({
                value: p.id,
                label: `${p.name}（${p.code} / ${p.region}）`,
              }))}
            />
          </Field>
          <Field label="ホスト名" required hint="MX が指す FQDN。PTR と一致させること。">
            <Input
              value={form.hostname}
              onChange={(e) => setForm({ ...form, hostname: e.target.value })}
              placeholder="mx1.example.jp"
              autoComplete="off"
            />
          </Field>
          <Field label="表示名" hint="省略するとホスト名をそのまま使います。">
            <Input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="リージョン" required>
            <Input
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              placeholder="tokyo"
              autoComplete="off"
            />
          </Field>
          <Field label="役割">
            <NativeSelect
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              options={NODE_ROLE.map((r) => ({ value: r, label: labelOf(r) }))}
            />
          </Field>
          <Field label="収容上限（メールボックス数）" hint="空欄ならプール既定。">
            <Input
              type="number"
              min={1}
              value={form.maxMailboxes}
              onChange={(e) => setForm({ ...form, maxMailboxes: e.target.value })}
            />
          </Field>
          <Field label="収容上限（ドメイン数）" hint="空欄ならプール既定。">
            <Input
              type="number"
              min={1}
              value={form.maxDomains}
              onChange={(e) => setForm({ ...form, maxDomains: e.target.value })}
            />
          </Field>
        </div>
      </FormDialog>

      <SecretRevealDialog
        open={result !== null}
        onOpenChange={() => setResult(null)}
        title="登録トークンを発行しました"
        description="このトークンは今だけ表示されます。ノードのインストーラに渡してください（登録が成功するとインストーラが削除します）。"
        items={
          result
            ? [
                { label: 'ノード ID', value: result.nodeId },
                { label: '登録トークン', value: result.token.enrollmentToken },
                { label: 'コントロールプレーン URL', value: result.token.controlPlaneUrl },
                { label: '有効期限', value: `発行から ${result.token.expiresInHours} 時間` },
              ]
            : []
        }
      />
    </>
  );
}

/* ---------------------------------------------------------- ノード削除 */

function DeleteNodeDialog({
  node,
  onClose,
}: {
  node: NodeSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.del(`/nodes/${node?.id ?? ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodes() });
      onClose();
    },
  });

  if (!node) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="ノードを削除"
      description="メールボックスが 0 件かつ退避済みのノードだけ削除できます。条件を満たさない場合はサーバー側で拒否されます。"
      confirmText={node.hostname}
      confirmLabel="削除する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onConfirm={() => mut.mutate()}
    >
      <p className="text-sm text-muted-foreground">
        収容中: メールボックス {formatCount(node.mailboxCount)} 件 / ドメイン {formatCount(node.domainCount)} 件
      </p>
      {mut.isError ? <p className="text-xs text-destructive">{errorMessageOf(mut.error)}</p> : null}
    </ConfirmTextDialog>
  );
}

